import type { Budget, BudgetItemType } from '../../types/budget'

export type AssignmentOption = {
  id: string
  label: string
}

const EXPENSE_TYPES = new Set<BudgetItemType>(['expense_fixed', 'expense_variable', 'debt', 'saving'])
const INCOME_TYPES = new Set<BudgetItemType>(['income_fixed', 'income_variable'])

export function budgetAssignmentId(id: number) {
  return `b-${id}`
}

function parsePrefixedId(value: string | number | null | undefined, prefix: string): number | null {
  const raw = String(value ?? '').trim()
  if (!raw.startsWith(prefix)) {
    return null
  }
  const id = Number(raw.slice(prefix.length))
  if (Number.isFinite(id) && id > 0) {
    return id
  }
  return null
}

export function parseBudgetItemId(value: string | number | null | undefined): number | null {
  return parsePrefixedId(value, 'b-')
}

export function spendFundAssignmentId(id: number) {
  return `s-${id}`
}

export function repayFundAssignmentId(id: number) {
  return `r-${id}`
}

export function parseSpendFundId(value: string | number | null | undefined): number | null {
  return parsePrefixedId(value, 's-')
}

export function parseRepayFundId(value: string | number | null | undefined): number | null {
  return parsePrefixedId(value, 'r-')
}

export function pagoCuotaLabel(name: string) {
  const detail = String(name || '').trim()
  return detail ? `Pago cuota: ${detail}` : 'Pago cuota'
}

export function gastoFondoLabel(name: string) {
  const detail = String(name || '').trim()
  return detail ? `Gasto en fondo: ${detail}` : 'Gasto en fondo'
}

export function assignmentFromDetail(detail: { budgetItemId?: number | null } | null | undefined): string | null {
  if (detail?.budgetItemId) {
    return budgetAssignmentId(detail.budgetItemId)
  }
  return null
}

export function assignmentOptions(
  budgets: Budget[],
  _date: Date | string | null | undefined,
  kind: 'I' | 'E',
): AssignmentOption[] {
  const allowed = kind === 'I' ? INCOME_TYPES : EXPENSE_TYPES
  const current = [...budgets].filter((budget) => budget.enabled).sort((a, b) => b.id - a.id)[0]
  if (!current) {
    return []
  }
  return (current.items || [])
    .filter((item) => item.id && allowed.has(item.type))
    .map((item) => ({
      id: budgetAssignmentId(item.id as number),
      label: item.detail,
    }))
}

export const UNASSIGNED_BUDGET = 'none'

export function budgetFilterOptions(budgets: Budget[], kind: 'I' | 'E'): AssignmentOption[] {
  const allowed = kind === 'I' ? INCOME_TYPES : EXPENSE_TYPES
  const seen = new Set<number>()
  const options: AssignmentOption[] = []
  for (const budget of budgets) {
    for (const item of budget.items || []) {
      if (!item.id || seen.has(item.id) || !allowed.has(item.type)) continue
      seen.add(item.id)
      options.push({ id: budgetAssignmentId(item.id), label: item.detail })
    }
  }
  options.push({
    id: UNASSIGNED_BUDGET,
    label: kind === 'I' ? 'Ingresos sin presupuesto' : 'Egresos sin presupuesto',
  })
  return options
}
